import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import offerLettersService from '../../services/offerLettersService';
import employeeService from '../../services/employeeService';
import applicantScreeningService from '../../services/applicantScreeningService';
import jobApplicationService from '../../services/jobApplicationService';
import companyService from '../../services/companyService';
import NotificationToast from '../../components/ui/NotificationToast';
import Select from '../../components/ui/Select';

const OfferLetters = () => {
  const { userProfile } = useAuth();
  const canManageSensitive = userProfile?.role === 'super_admin';
  const [letters, setLetters] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [loadingApplicants, setLoadingApplicants] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [editingLetter, setEditingLetter] = useState(null);
  const [notification, setNotification] = useState(null);
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [formData, setFormData] = useState({
    employee_id: '', applicant_id: '', position: '', department: '',
    offered_salary: '', joining_date: '', content: '', status: 'draft'
  });

  useEffect(() => {
    loadLetters();
    loadEmployees();
    loadApplicants();
  }, []);

  // Cleanup blob URL when preview closes or component unmounts
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);


  const openPreviewFromContent = (content) => {
    if (!content) {
      setNotification({ message: 'No content to preview', type: 'error' });
      return;
    }

    setPreviewContent(content);

    const cleanContent = content
      .replace(/\/\*# sourceMappingURL=.*?\*\//g, '')
      .replace(/\/\/# sourceMappingURL=.*?$/gm, '')
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');

    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }

    const blob = new Blob([cleanContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    setPreviewUrl(url);
    setShowPreview(true);
  };

  const handlePreviewSavedLetter = async (letter) => {
    setGeneratingPreview(true);
    try {
      let content = letter.content;
      if (!content) {
        const result = await offerLettersService.getOfferLetter(letter.id);
        if (result?.success && result.data?.content) {
          content = result.data.content;
        }
      }
      if (!content) {
        setNotification({ message: 'No content available to preview', type: 'error' });
        return;
      }
      openPreviewFromContent(content);
    } catch (error) {
      setNotification({ message: 'Error loading preview', type: 'error' });
    } finally {
      setGeneratingPreview(false);
    }
  };

  const generateTemplate = async () => {
    if (!formData.position || !formData.offered_salary || !formData.joining_date) {
      setNotification({ message: 'Please fill in Position, Salary, and Joining Date first', type: 'error' });
      return;
    }

    if (!formData.employee_id && !formData.applicant_id) {
      setNotification({ message: 'Please select either an Employee or Applicant', type: 'error' });
      return;
    }

    setGeneratingPreview(true);
    try {
      // Create a temporary letter to generate template
      const tempData = {
        ...formData,
        employee_name: formData.employee_id ? employees.find(e => e.id === formData.employee_id)?.full_name : null,
        applicant_name: formData.applicant_id ? applicants.find(a => a.id === formData.applicant_id)?.applicant_name : null,
        employee_email: formData.employee_id ? employees.find(e => e.id === formData.employee_id)?.email : null,
        applicant_email: formData.applicant_id ? applicants.find(a => a.id === formData.applicant_id)?.email : null,
      };

      // Create temporary letter to get generated content
      const result = await offerLettersService.createOfferLetter({
        ...tempData,
        content: '' // Let backend generate
      });

      if (result?.success && result.data?.content) {
        setFormData(prev => ({ ...prev, content: result.data.content }));
        setNotification({ message: 'Professional template generated successfully', type: 'success' });
        // Delete the temporary letter
        if (result.data.id) {
          await offerLettersService.deleteOfferLetter(result.data.id);
        }
      } else {
        setNotification({ message: 'Failed to generate template', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'Error generating template', type: 'error' });
    } finally {
      setGeneratingPreview(false);
    }
  };

  const handlePreview = async () => {
    if (!formData.position || !formData.offered_salary || !formData.joining_date) {
      setNotification({ message: 'Please fill in Position, Salary, and Joining Date first', type: 'error' });
      return;
    }

    setGeneratingPreview(true);
    try {
      // Create temporary letter to get generated content with fresh company data
      const tempData = {
        ...formData,
        employee_name: formData.employee_id ? employees.find(e => e.id === formData.employee_id)?.full_name : null,
        applicant_name: formData.applicant_id ? applicants.find(a => a.id === formData.applicant_id)?.applicant_name : null,
        employee_email: formData.employee_id ? employees.find(e => e.id === formData.employee_id)?.email : null,
        applicant_email: formData.applicant_id ? applicants.find(a => a.id === formData.applicant_id)?.email : null,
      };

      // Always regenerate template to get fresh company data
      // Pass empty content to force backend to regenerate with latest company info
      const result = await offerLettersService.createOfferLetter({
        ...tempData,
        content: '' // Force regeneration to get fresh company data
      });

      if (result?.success && result.data?.content) {
        const content = result.data.content || '';
        openPreviewFromContent(content);
        // Delete temporary letter
        if (result.data.id) {
          await offerLettersService.deleteOfferLetter(result.data.id);
        }
      } else {
        setNotification({ message: 'Failed to generate preview', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'Error generating preview', type: 'error' });
    } finally {
      setGeneratingPreview(false);
    }
  };

  const handleDownload = async (letter) => {
    try {
      const recipientName = letter.employee_name || letter.applicant_name || 'Offer_Letter';
      const result = await offerLettersService.downloadOfferLetter(letter.id, recipientName);
      if (result?.success) {
        setNotification({ message: 'Offer letter downloaded as PDF', type: 'success' });
      } else {
        setNotification({ message: result?.error || 'Failed to download', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'Error downloading offer letter', type: 'error' });
    }
  };

  const loadLetters = async () => {
    try {
      setLoading(true);
      const result = await offerLettersService.getOfferLetters();
      console.log('Offer letters API response:', result);
      if (result?.success) {
        setLetters(Array.isArray(result.data) ? result.data : []);
      } else {
        // Safely extract and log error message
        const errorMsg = result?.error || result?.message || 'Unknown error';
        const safeErrorMsg = typeof errorMsg === 'string' ? errorMsg : String(errorMsg);
        console.error('Failed to load offer letters:', safeErrorMsg);
        setLetters([]);
      }
    } catch (error) {
      // Safely handle any error
      let errorMsg = 'Unknown error occurred';
      try {
        if (error && typeof error === 'object') {
          errorMsg = error.message || error.error || JSON.stringify(error);
        } else {
          errorMsg = String(error);
        }
      } catch (e) {
        errorMsg = 'Error occurred while loading offer letters';
      }
      console.error('Error loading offer letters:', errorMsg);
      setLetters([]);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const result = await employeeService.getEmployees();
      if (result?.success) {
        const employeeList = result.data || [];
        setEmployees(employeeList);
        console.log('Employees loaded successfully:', employeeList.length, employeeList);
      } else {
        console.error('Failed to load employees:', result?.error);
        setEmployees([]);
        // Don't show error notification immediately, only if user tries to use it
      }
    } catch (error) {
      console.error('Error loading employees:', error);
      setEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const loadApplicants = async () => {
    try {
      setLoadingApplicants(true);
      // Only the Applicant whose Status is Selected should be present in dropdown
      const result = await jobApplicationService.getApplications({ status: 'selected' });
      if (result?.success) {
        const applicantList = result.data || [];
        // Extract name and id, handling potential variations in field names
        const formattedApplicants = applicantList.map(app => ({
          id: app.id,
          applicant_name: app.applicant_name || `${app.first_name || ''} ${app.last_name || ''}`.trim(),
          position: app.position,
          email: app.email
        }));
        setApplicants(formattedApplicants);
        console.log('Selected applicants loaded:', formattedApplicants.length);
      } else {
        console.error('Failed to load selected applicants:', result?.error);
        setApplicants([]);
      }
    } catch (error) {
      console.error('Error loading applicants:', error);
      setApplicants([]);
    } finally {
      setLoadingApplicants(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canManageSensitive) {
      setNotification({ message: 'You do not have permission to manage offer letters.', type: 'error' });
      return;
    }
    try {
      const result = editingLetter
        ? await offerLettersService.updateOfferLetter(editingLetter.id, formData)
        : await offerLettersService.createOfferLetter(formData);

      if (result?.success) {
        setNotification({ message: `Offer letter ${editingLetter ? 'updated' : 'created'} successfully`, type: 'success' });
        setShowModal(false);
        setEditingLetter(null);
        setFormData({ employee_id: '', applicant_id: '', position: '', department: '', offered_salary: '', joining_date: '', content: '', status: 'draft' });
        loadLetters();
      } else {
        setNotification({ message: result?.error || 'Operation failed', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    }
  };

  const handleEdit = (letter) => {
    if (!canManageSensitive) {
      setNotification({ message: 'You do not have permission to edit offer letters.', type: 'error' });
      return;
    }
    setEditingLetter(letter);
    setFormData({
      employee_id: letter.employee_id || '',
      applicant_id: letter.applicant_id || '',
      position: letter.position || '',
      department: letter.department || '',
      offered_salary: letter.offered_salary || '',
      joining_date: letter.joining_date || '',
      content: letter.content || '',
      status: letter.status || 'draft'
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!canManageSensitive) {
      setNotification({ message: 'You do not have permission to delete offer letters.', type: 'error' });
      return;
    }
    if (!window.confirm('Are you sure you want to delete this offer letter?')) return;
    try {
      const result = await offerLettersService.deleteOfferLetter(id);
      if (result?.success) {
        setNotification({ message: 'Offer letter deleted successfully', type: 'success' });
        loadLetters();
      } else {
        setNotification({ message: result?.error || 'Failed to delete', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6">
      <NotificationToast message={notification?.message} type={notification?.type} onDismiss={() => setNotification(null)} />

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Offer Letters</h2>
          <p className="text-sm text-muted-foreground mt-1">Generate and manage employment offer letters</p>
        </div>
        <Button
          onClick={() => { setEditingLetter(null); setFormData({ employee_id: '', applicant_id: '', position: '', department: '', offered_salary: '', joining_date: '', content: '', status: 'draft' }); setShowModal(true); }}
          disabled={!canManageSensitive}
          title={canManageSensitive ? 'Create offer letter' : 'Only super admins can create offer letters'}
        >
          <Icon name="Plus" size={16} className="mr-2" />
          Create Offer Letter
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" /><p className="text-muted-foreground">Loading...</p></div>
      ) : (
        <div className="bg-background border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Recipient</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Position</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Department</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Salary</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Joining Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-foreground uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {letters.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-muted-foreground">
                      <Icon name="FileText" size={48} className="mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium mb-2">No offer letters found</p>
                      <p className="text-sm">Create your first offer letter using the button above</p>
                    </td>
                  </tr>
                ) : (
                  letters.map(letter => (
                    <tr key={letter.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium text-foreground">{letter.employee_name || letter.applicant_name || 'N/A'}</div>
                        <div className="text-sm text-muted-foreground">
                          {letter.employee_email || letter.applicant_email || ''}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground">{letter.position || '-'}</td>
                      <td className="px-6 py-4 text-sm text-foreground">{letter.department || '-'}</td>
                      <td className="px-6 py-4 text-sm text-foreground">
                        ₹{parseFloat(letter.offered_salary || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="px-6 py-4 text-sm text-foreground">
                        {letter.joining_date ? new Date(letter.joining_date).toLocaleDateString('en-IN') : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(letter.status)}`}>
                          {letter.status?.charAt(0).toUpperCase() + letter.status?.slice(1) || 'Draft'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">
                        {letter.created_at ? new Date(letter.created_at).toLocaleDateString('en-IN') : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handlePreviewSavedLetter(letter)} title="Preview" disabled={generatingPreview}>
                            <Icon name="Eye" size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDownload(letter)} title="Download PDF">
                            <Icon name="Download" size={14} />
                          </Button>
                          {canManageSensitive && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleEdit(letter)} title="Edit">
                                <Icon name="Edit" size={14} />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(letter.id)} className="text-destructive" title="Delete">
                                <Icon name="Trash2" size={14} />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowModal(false); setEditingLetter(null); }}>
          <div className="bg-background rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{editingLetter ? 'Edit' : 'Create'} Offer Letter</h3>
              <Button variant="ghost" size="sm" onClick={() => { setShowModal(false); setEditingLetter(null); }}>
                <Icon name="X" size={20} />
              </Button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Select Employee</label>
                  {loadingEmployees ? (
                    <div className="p-3 bg-muted/50 rounded-md text-sm text-muted-foreground">
                      <p>Loading employees...</p>
                    </div>
                  ) : employees.length > 0 ? (
                    <Select
                      options={[
                        { value: '', label: 'Select Employee' },
                        ...employees.map(emp => ({
                          value: emp.id,
                          label: (emp.full_name || emp.user_full_name || emp.name || 'Unknown Employee') + (emp.employee_id ? ` (${emp.employee_id})` : '')
                        }))
                      ]}
                      value={formData.employee_id}
                      onValueChange={(value) => setFormData({ ...formData, employee_id: value, applicant_id: '' })}
                      placeholder="Choose an employee..."
                      searchable
                    />
                  ) : (
                    <div className="p-3 bg-muted/50 rounded-md text-sm text-muted-foreground">
                      <p className="mb-2">No employees available.</p>
                      <p className="text-xs">Go to <strong>Company Config → Employee List</strong> to add employees first.</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Or Select Applicant</label>
                  {loadingApplicants ? (
                    <div className="p-3 bg-muted/50 rounded-md text-sm text-muted-foreground">
                      <p>Loading applicants...</p>
                    </div>
                  ) : applicants.length > 0 ? (
                    <Select
                      options={[
                        { value: '', label: 'Select Applicant' },
                        ...applicants.map(app => ({
                          value: app.id,
                          label: (app.applicant_name || app.name || 'Unknown Applicant') + (app.position ? ` - ${app.position}` : '')
                        }))
                      ]}
                      value={formData.applicant_id}
                      onValueChange={(value) => setFormData({ ...formData, applicant_id: value, employee_id: '' })}
                      placeholder="Choose an applicant..."
                      searchable
                    />
                  ) : (
                    <div className="p-3 bg-muted/50 rounded-md text-sm text-muted-foreground">
                      <p className="mb-2">No applicants available.</p>
                      <p className="text-xs">Go to <strong>Applicant Tracking System</strong> to add applicants first.</p>
                      <p className="text-xs mt-1">You can still create an offer letter manually without selecting an applicant.</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Position *" value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} required />
                <Input label="Department" value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} />
                <Input label="Offered Salary (₹) *" type="number" value={formData.offered_salary} onChange={(e) => setFormData({ ...formData, offered_salary: e.target.value })} required />
                <Input label="Joining Date *" type="date" value={formData.joining_date} onChange={(e) => setFormData({ ...formData, joining_date: e.target.value })} required />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">Letter Content</label>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={generateTemplate} disabled={generatingPreview || !formData.position || !formData.offered_salary || !formData.joining_date || (!formData.employee_id && !formData.applicant_id)}>
                      <Icon name="FileText" size={14} className="mr-1" />
                      {generatingPreview ? 'Generating...' : 'Generate Template'}
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={handlePreview} disabled={generatingPreview || !formData.content}>
                      <Icon name="Eye" size={14} className="mr-1" />
                      Preview
                    </Button>
                  </div>
                </div>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-md font-mono text-sm"
                  rows="12"
                  placeholder="Click 'Generate Template' to create a professional offer letter template, or write your own content. You can edit the generated template as needed."
                />
                {generatingPreview && (
                  <p className="text-xs text-muted-foreground mt-1">Generating professional template...</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Status</label>
                <Select
                  options={[
                    { value: 'draft', label: 'Draft' },
                    { value: 'sent', label: 'Sent' },
                    { value: 'accepted', label: 'Accepted' },
                    { value: 'rejected', label: 'Rejected' },
                    { value: 'Moved Deployment', label: 'Moved to Deployment' }
                  ]}
                  value={formData.status}
                  onChange={(value) => setFormData({ ...formData, status: value })}
                />
              </div>
              <div className="flex gap-3 justify-end pt-4 border-t border-border">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); setEditingLetter(null); }}>Cancel</Button>
                <Button type="submit" disabled={generatingPreview}>
                  {generatingPreview ? 'Saving...' : 'Save Offer Letter'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPreview && previewContent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowPreview(false)}>
          <div className="bg-background rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Offer Letter Preview</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)}>
                <Icon name="X" size={20} />
              </Button>
            </div>
            <div className="border border-border rounded-lg bg-white overflow-hidden">
              <iframe
                src={previewUrl || undefined}
                className="w-full h-[70vh] border-0"
                title="Offer Letter Preview"
                sandbox="allow-same-origin"
                onLoad={(e) => {
                  // Suppress source map errors by removing any source map references
                  const iframe = e.target;
                  try {
                    if (iframe.contentWindow && iframe.contentDocument) {
                      // Remove any source map links or comments
                      const scripts = iframe.contentDocument.querySelectorAll('script');
                      scripts.forEach(script => {
                        if (script.src && script.src.includes('sourcemap')) {
                          script.remove();
                        }
                      });

                      // Remove source map comments from style tags
                      const styles = iframe.contentDocument.querySelectorAll('style');
                      styles.forEach(style => {
                        style.textContent = style.textContent.replace(/\/\*# sourceMappingURL=.*?\*\//g, '');
                      });
                    }
                  } catch (err) {
                    // Ignore cross-origin errors
                    console.debug('Preview iframe loaded');
                  }
                }}
                onError={() => {
                  // Fallback to data URL if blob URL fails
                  if (!previewUrl && previewContent) {
                    const cleanContent = previewContent
                      .replace(/\/\*# sourceMappingURL=.*?\*\//g, '')
                      .replace(/\/\/# sourceMappingURL=.*?$/gm, '')
                      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
                    // Use a data URL as fallback
                    const dataUrl = 'data:text/html;charset=utf-8,' + encodeURIComponent(cleanContent);
                    setPreviewUrl(dataUrl);
                  }
                }}
              />
            </div>
            <div className="flex gap-3 justify-end mt-4 pt-4 border-t border-border">
              <Button variant="outline" onClick={() => {
                setShowPreview(false);
                // Clean up blob URL
                if (previewUrl && previewUrl.startsWith('blob:')) {
                  URL.revokeObjectURL(previewUrl);
                  setPreviewUrl('');
                }
              }}>Close</Button>
              <Button onClick={() => {
                // Create a new window for printing with header on each page
                const printWindow = window.open('', '_blank');
                if (printWindow) {
                  // Add script to repeat header on each page
                  const contentWithScript = previewContent.replace(
                    '</head>',
                    `<script>
                      window.onload = function() {
                        // Function to add header to each page when printing
                        const style = document.createElement('style');
                        style.textContent = \`
                          @media print {
                            @page {
                              margin-top: 100px;
                            }
                            .page-header {
                              position: fixed;
                              top: 0;
                              left: 0;
                              right: 0;
                              background: white;
                              z-index: 1000;
                            }
                            .page-header thead {
                              display: table-header-group;
                            }
                          }
                        \`;
                        document.head.appendChild(style);
                      };
                    </script>
                    </head>`
                  );
                  printWindow.document.write(contentWithScript);
                  printWindow.document.close();
                  printWindow.onload = () => {
                    setTimeout(() => {
                      printWindow.print();
                    }, 500);
                  };
                } else {
                  // Fallback to current window print
                  window.print();
                }
              }}>
                <Icon name="Printer" size={16} className="mr-2" />
                Print / Save as PDF
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OfferLetters;

